/**
 * Describes how to compute the `scope` object passed into rule predicates:
 * `conditions.when(ctx, value, scope)`.
 *
 * At config registration time, the engine precomputes (per leaf rule key) whether the nearest
 * relevant wrapper context is:
 * - a `$group` (scope = the group root object), or
 * - a `$collection` (scope = the *indexed element* containing the leaf).
 *
 * The binding is later resolved at runtime by `getConditionScopeForPath()`.
 */
export type ScopeBinding = { kind: 'group' | 'collection'; root: string };

/**
 * Picks the effective scope binding for a leaf.
 *
 * Precedence:
 * - Collections win over groups.
 *   When validating `items[2].name`, the most useful `scope` for a `when(...)` predicate is the
 *   specific element `items[2]` (not the whole `items` array or an outer group).
 * - If a collection binding is not present, fall back to the first group root.
 */
export function getScopeBinding(scopeRootPath?: string, collectionRootPath?: string): ScopeBinding | undefined {
    if (collectionRootPath) return { kind: 'collection', root: collectionRootPath };
    if (scopeRootPath) return { kind: 'group', root: scopeRootPath };
    return undefined;
}
